import { useMemo } from "react";
import { Tooltip, Box, TableCell } from "@mui/material";
import { getRoleNameByRoleMrn } from "~/components/Members/utils/membersUtils";
import type { Role } from "~/lib/iam/roles";
import { useRoleCellFittingItems } from "./hooks/useRoleCellFittingItems";

interface RoleCellProps {
  roles: Array<string | { mrn: string } | null>;
}

/**
 * Displays role names in a table cell, showing as many as fit within the cell width.
 * Roles that don't fit are hidden behind a "+N more" tooltip.
 */
export const RoleCell = ({ roles }: RoleCellProps) => {
  const roleNames = useMemo<Role["title"][]>(
    () =>
      roles
        .map((role) => {
          const mrn = typeof role === "string" ? role : role?.mrn;
          return getRoleNameByRoleMrn(mrn || "");
        })
        .filter((name): name is Role["title"] => Boolean(name)),
    [roles],
  );
  const { containerRef, measureRef, hasHidden, hiddenItems, displayText } =
    useRoleCellFittingItems({ items: roleNames });

  if (roleNames.length === 0) {
    return <TableCell>—</TableCell>;
  }

  const content = hasHidden ? (
    <Tooltip title={hiddenItems.join(", ")}>
      <span style={{ cursor: "help" }}>{displayText}</span>
    </Tooltip>
  ) : (
    displayText
  );

  return (
    <TableCell>
      <Box ref={containerRef} sx={{ width: 1, whiteSpace: "nowrap" }}>
        <span
          ref={measureRef}
          aria-hidden="true"
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "nowrap",
          }}
        />
        {content}
      </Box>
    </TableCell>
  );
};
